import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { loginStaff } from '../api/staff.js';

const REMEMBERED_EMAIL_KEY = 'ldsurvey_remembered_email';
const REMEMBERED_PASSWORD_KEY = 'ldsurvey_remembered_password';
const LEGACY_REMEMBERED_USERNAME_KEY = 'ldsurvey_remembered_username';

const inputClass =
  'w-full rounded-lg border border-slate-300 px-4 py-3 text-base font-semibold text-slate-900 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20';

function Login({ onLogin }) {
  const location = useLocation();
  const successMessage = location.state?.message;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedEmail =
      localStorage.getItem(REMEMBERED_EMAIL_KEY) ||
      localStorage.getItem(LEGACY_REMEMBERED_USERNAME_KEY);
    const savedPassword = localStorage.getItem(REMEMBERED_PASSWORD_KEY);

    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    if (savedPassword) {
      setPassword(savedPassword);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await loginStaff(email.trim(), password);

      if (!result.ok) {
        setError(result.error || 'Login failed');
        return;
      }

      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim().toLowerCase());
        localStorage.setItem(REMEMBERED_PASSWORD_KEY, password);
        localStorage.removeItem(LEGACY_REMEMBERED_USERNAME_KEY);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
        localStorage.removeItem(REMEMBERED_PASSWORD_KEY);
        localStorage.removeItem(LEGACY_REMEMBERED_USERNAME_KEY);
      }

      onLogin({ token: result.data.token, staff: result.data.staff });
    } catch {
      setError('Unable to connect to server. Check your network and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col bg-slate-50">
      <div className="bg-brand-primary px-6 py-10 text-white">
        <h1 className="text-2xl font-bold">LD Internal Ambassador</h1>
      </div>

      <div className="flex-1 p-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-center text-xl font-semibold text-slate-800">Login</h2>
          {successMessage && (
            <p className="mb-4 rounded-lg bg-green-50 px-3 py-2 text-center text-sm text-green-700">
              {successMessage}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                JTI Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="your@jtiemail.com"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-12`}
                  placeholder="Your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-accent focus:ring-brand-accent"
              />
              <span className="text-sm text-slate-700">Remember me</span>
            </label>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand-accent py-3 text-base font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Logging in…' : 'Login'}
            </button>
          </form>

          <div className="mt-6 space-y-2 text-center text-sm">
            <p>
              <Link to="/forgot-password" className="font-medium text-brand-accent hover:underline">
                Forgot password?
              </Link>
            </p>
            <p className="text-slate-600">
              No account?{' '}
              <Link to="/register" className="font-medium text-brand-accent hover:underline">
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
